/* ============================================================
   KKUMI — SLEEVE FINDER navigator

   The quiz is server-rendered from the section's Step / Option /
   Result blocks (see sections/kkumi-sleeve-finder.liquid). Every
   screen is in the DOM, hidden, keyed by data-step / data-result.

   Answers accumulate rather than branching. Each Step records one
   answer key (data-facet); each Option sets the value (data-value);
   a Step is only asked when its condition (data-when) holds; and a
   Result declares the combination it wins on (data-match). So a
   question that applies to several paths is authored ONCE — the
   cost of a new question is one Step block, not a duplicated
   subtree per branch.

   Condition grammar, shared by data-when and data-match:
     "type=clear"                    one answer must equal clear
     "type=clear|coloured"           | = either value will do
     "card=standard, type=clear"     , = both terms must hold
     "type=coloured; protect=showoff"  ; = either group will do
     ""                              always (step) / catch-all (result)

   Zero merchant copy is built here — it's all in the blocks — so
   there is nothing to escape.
   ============================================================ */
(function () {
  'use strict';

  var CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>';

  /* Parses into OR-groups of AND-terms:
     "card=standard, type=clear; card=minitour"
       -> [ [{k:'card',vals:['standard']}, {k:'type',vals:['clear']}],
            [{k:'card',vals:['minitour']}] ]
     An empty result means "no condition" — always, or catch-all. */
  function parseCond(str) {
    return String(str || '').split(';').map(function (group) {
      var terms = [];
      group.split(',').forEach(function (term) {
        term = term.trim();
        var eq = term.indexOf('=');
        if (eq < 0) return;
        var key = term.slice(0, eq).trim();
        var vals = term.slice(eq + 1).split('|').map(function (v) { return v.trim(); }).filter(Boolean);
        if (key && vals.length) terms.push({ k: key, vals: vals });
      });
      return terms;
    }).filter(function (terms) { return terms.length; });
  }

  /* Strict — an unanswered key fails. Drives which step is asked next and
     which result wins, both of which run only once the answers are in. */
  function holds(cond, answers) {
    if (!cond.length) return true;
    return cond.some(function (terms) {
      return terms.every(function (t) {
        return answers[t.k] != null && t.vals.indexOf(answers[t.k]) >= 0;
      });
    });
  }

  /* Lenient — an unanswered key is "could still go either way", but ONLY if
     some step that is itself still reachable can answer it. Used for the
     progress row, so a pip stays lit while its step is genuinely possible and
     disappears once it is ruled out. */
  function stillPossible(cond, answers, settable) {
    if (!cond.length) return true;
    return cond.some(function (terms) {
      return terms.every(function (t) {
        if (answers[t.k] != null) return t.vals.indexOf(answers[t.k]) >= 0;
        return !!settable[t.k];
      });
    });
  }

  function initFinder(root) {
    if (root.dataset.kkBound) return;
    root.dataset.kkBound = '1';

    var stepEls = Array.prototype.slice.call(root.querySelectorAll('[data-step]'));
    var resultEls = Array.prototype.slice.call(root.querySelectorAll('[data-result]'));
    var pipEls = Array.prototype.slice.call(root.querySelectorAll('[data-pip]'));
    var sepEls = Array.prototype.slice.call(root.querySelectorAll('[data-pip-sep]'));
    if (!stepEls.length || !resultEls.length) return;

    var steps = stepEls.map(function (el) {
      return {
        el: el,
        facet: el.getAttribute('data-facet') || '',
        cond: parseCond(el.getAttribute('data-when'))
      };
    });
    var results = resultEls.map(function (el) {
      var cond = parseCond(el.getAttribute('data-match'));
      return { el: el, cond: cond, isCatchAll: !cond.length };
    });

    var answers = {};
    var asked = []; // step indices answered, in order — the Back stack

    /* Next step at or after `from` whose condition holds. -1 = none left. */
    function nextStep(from) {
      for (var i = from; i < steps.length; i++) {
        if (holds(steps[i].cond, answers)) return i;
      }
      return -1;
    }

    /* First specific match in block order; otherwise the first catch-all;
       otherwise the first result — the quiz never dead-ends on a blank screen. */
    function pickResult() {
      for (var i = 0; i < results.length; i++) {
        if (!results[i].isCatchAll && holds(results[i].cond, answers)) return results[i].el;
      }
      for (var j = 0; j < results.length; j++) {
        if (results[j].isCatchAll) return results[j].el;
      }
      return results[0].el;
    }

    /* Which steps are still reachable, as a fixpoint.
       Ruling out one step can rule out others: answering "mini tour" kills the
       sleeve-type step, which means nothing can ever set `type`, which in turn
       kills the protection step that asks for type=clear, and so on. So keep
       sweeping — recomputing which answer keys are still settable — until a
       pass rules nothing further out. */
    function reachableSteps() {
      var possible = steps.map(function () { return true; });
      for (var pass = 0; pass < steps.length; pass++) {
        var settable = {};
        steps.forEach(function (s, i) { if (possible[i] && s.facet) settable[s.facet] = true; });
        var changed = false;
        steps.forEach(function (s, i) {
          if (!possible[i]) return;
          if (stillPossible(s.cond, answers, settable)) return;
          possible[i] = false;
          changed = true;
        });
        if (!changed) break;
      }
      return possible;
    }

    function renderPips(currentIdx, done) {
      // A pip is shown while its step is still reachable given the answers so
      // far; separators follow the pips they sit between.
      var possible = reachableSteps();
      var visible = [];
      steps.forEach(function (s, i) {
        pipEls[i].hidden = !possible[i];
        if (possible[i]) visible.push(i);
      });
      sepEls.forEach(function (sep, i) {
        // sepEls[i] is rendered immediately before pipEls[i + 1].
        var after = pipEls[i + 1];
        var anyBefore = pipEls.slice(0, i + 1).some(function (p) { return !p.hidden; });
        sep.hidden = !(anyBefore && after && !after.hidden);
      });

      var pos = visible.indexOf(currentIdx);
      visible.forEach(function (stepIdx, n) {
        var pip = pipEls[stepIdx];
        var isDone = done || (pos >= 0 && n < pos);
        var isActive = !done && n === pos;
        pip.classList.toggle('is-done', isDone);
        pip.classList.toggle('is-active', isActive);
        var mark = pip.querySelector('[data-pip-mark]');
        if (mark) mark.innerHTML = isDone ? CHECK : String(n + 1);
      });
    }

    function hideAll() {
      stepEls.concat(resultEls).forEach(function (el) {
        el.hidden = true;
        el.classList.remove('is-current', 'finder__fade');
      });
    }

    function reveal(el) {
      hideAll();
      el.hidden = false;
      el.classList.add('is-current');
      void el.offsetWidth; // restart the fade
      el.classList.add('finder__fade');
    }

    function showStep(idx) {
      reveal(steps[idx].el);
      renderPips(idx, false);
      var back = steps[idx].el.querySelector('.finder__back');
      if (back) back.hidden = asked.length === 0;
    }

    function showResult() {
      reveal(pickResult());
      renderPips(-1, true);
    }

    /* Move to the next applicable step after `from`, or to the result. */
    function advance(from) {
      var idx = nextStep(from);
      if (idx < 0) showResult();
      else showStep(idx);
    }

    function reset() {
      answers = {};
      asked = [];
      advance(0);
    }

    function handleShop(e, a) {
      var id = a.getAttribute('data-scroll-target');
      var target = id ? document.getElementById(id) : null;
      if (!target) return; // no on-page target — let the href navigate
      e.preventDefault();
      var y = target.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: y, behavior: 'smooth' });
      var flash = (a.getAttribute('data-flash') || id).split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
      flash.forEach(function (fid) {
        var c = document.getElementById(fid);
        if (c) { c.classList.add('is-flash'); setTimeout(function () { c.classList.remove('is-flash'); }, 1600); }
      });
    }

    root.addEventListener('click', function (e) {
      var opt = e.target.closest('.finder-opt');
      if (opt && root.contains(opt)) {
        var stepEl = opt.closest('[data-step]');
        var idx = stepEl ? stepEls.indexOf(stepEl) : -1;
        if (idx < 0) return;
        var value = opt.getAttribute('data-value');
        if (steps[idx].facet && value) answers[steps[idx].facet] = value;
        asked.push(idx);
        advance(idx + 1);
        return;
      }
      if (e.target.closest('[data-finderback]')) {
        var prev = asked.pop();
        if (prev == null) return;
        // Un-answer the step we're returning to; anything asked after it was
        // already popped, so no later answer can be left stranded.
        if (steps[prev].facet) delete answers[steps[prev].facet];
        showStep(prev);
        return;
      }
      if (e.target.closest('[data-restart]')) {
        reset();
        return;
      }
      var shop = e.target.closest('[data-shopcta]');
      if (shop && root.contains(shop)) handleShop(e, shop);
    });

    reset();
    warmResultPhotos(root);
  }

  /* Every result screen sits [hidden] until the quiz picks one, so the browser
     never fetches its .finder-rec__photo background-image — the photo only
     starts downloading at the moment it is revealed, and the reader watches an
     empty panel resolve. (The coloured backdrop used to paper over that; it is
     gone now, so the gap would be a blank panel.) Warm every result photo into
     the cache once the page is idle instead: by the time any result is shown
     the image is already there.
     Idle, not on load, so the quiz's own first paint is never delayed. */
  function warmResultPhotos(root) {
    var seen = Object.create(null);
    whenIdle(function () {
      root.querySelectorAll('.finder-rec__photo').forEach(function (el) {
        var m = /url\(["']?(.*?)["']?\)/.exec(el.style.backgroundImage || '');
        var src = m && m[1];
        if (!src || seen[src]) return;
        seen[src] = true;
        var img = new Image();
        img.decoding = 'async';
        img.src = src;
      });
    });
  }

  function whenIdle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 400);
  }

  function boot() { document.querySelectorAll('[data-finder]').forEach(initFinder); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
